import { AnalyticsDashboardDateRangePreset } from '../../graphql/__generated__/graphql';
import type { DoorVisitDateRangePreset } from '../filters/doorVisitFilters';

export const ANALYTICS_DATE_RANGE_OPTIONS = [
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
] as const;

export type AnalyticsDateRange = (typeof ANALYTICS_DATE_RANGE_OPTIONS)[number]['value'];

export const DEFAULT_ANALYTICS_DATE_RANGE: AnalyticsDateRange = 'last_7_days';

const DATE_RANGE_SEARCH_PARAM = 'range';

export const isAnalyticsDateRange = (value: string): value is AnalyticsDateRange =>
  ANALYTICS_DATE_RANGE_OPTIONS.some(option => option.value === value);

export const parseAnalyticsDateRange = (searchParams: URLSearchParams): AnalyticsDateRange => {
  const range = searchParams.get(DATE_RANGE_SEARCH_PARAM);
  return range !== null && isAnalyticsDateRange(range) ? range : DEFAULT_ANALYTICS_DATE_RANGE;
};

// The default remains implicit so the dashboard's ordinary URL is concise while
// every non-default selection is shareable and refresh-safe.
export const analyticsDateRangeToSearchParams = (
  searchParams: URLSearchParams,
  range: AnalyticsDateRange,
): URLSearchParams => {
  const nextSearchParams = new URLSearchParams(searchParams);
  if (range === DEFAULT_ANALYTICS_DATE_RANGE) {
    nextSearchParams.delete(DATE_RANGE_SEARCH_PARAM);
  } else {
    nextSearchParams.set(DATE_RANGE_SEARCH_PARAM, range);
  }
  return nextSearchParams;
};

export const analyticsDateRangeToGraphQLPreset = (
  range: AnalyticsDateRange,
): AnalyticsDashboardDateRangePreset => {
  switch (range) {
    case 'last_7_days':
      return AnalyticsDashboardDateRangePreset.Last_7Days;
    case 'last_30_days':
      return AnalyticsDashboardDateRangePreset.Last_30Days;
    case 'all':
      return AnalyticsDashboardDateRangePreset.AllTime;
  }
};

// Analytics supports a subset of the Door Visit URL presets. Keeping this
// conversion here ensures custom-slot drill-downs retain the dashboard scope.
export const analyticsDateRangeToDoorVisitDateRange = (
  range: AnalyticsDateRange,
): DoorVisitDateRangePreset => range;
